import "../shipment-details/shipment-details.css";

import { ButtonGroup } from "@tql/ui-components-react/kendo/buttons";
import { Loader } from "@tql/ui-components-react/kendo/indicators";
import {
	Card,
	CardBody,
	CardHeader,
} from "@tql/ui-components-react/kendo/layout";
import { Button } from "@tql/ui-components-react/tql/buttons";
import { memo } from "react";

import type { ShipmentManagementProgressDetails } from "~/shared/schemas/shipment-management-schema.ts";

import { useGetShipmentStatusEnums } from "../../hooks/use-get-shipment-status-enums.ts";
import type { AugmentedShipment } from "../../types/augmented-shipment";
import { DeliveredGroup } from "../delivered-group/delivered-group.tsx";
import { DispatchedGroup } from "../dispatched-group/dispatched-group.tsx";
import { NotDispatchedGroup } from "../not-dispatched-group/not-dispatched-group.tsx";
import { TransitMap } from "../transit-map/transit-map.tsx";

export const ShipmentProgress = memo(
	({
		details,
		shipment,
	}: {
		details?: ShipmentManagementProgressDetails;
		shipment: AugmentedShipment;
	}) => {
		const { shipmentStatusEnums, isLoading } = useGetShipmentStatusEnums();

		if (!details) {
			return null;
		}

		const childMapper = {
			0: <NotDispatchedGroup shipment={shipment} />,
			1: <DispatchedGroup shipment={shipment} />,
			2: <TransitMap shipment={shipment} />,
			3: <DeliveredGroup shipment={shipment} />,
		};

		return (
			<Card className={"shipment-progress-card"}>
				<CardHeader>
					<h3>Shipment Progress</h3>
				</CardHeader>
				<CardBody>
					<div className={"flex-header"}>
						<div className={"button-group"}>
							{isLoading ? (
								<div className={"shipment-progress-loader"}>
									<Loader
										size={"medium"}
										themeColor={"primary"}
										type={"infinite-spinner"}
									/>
								</div>
							) : (
								<ButtonGroup>
									{Object.keys(shipmentStatusEnums ?? {}).map((id) => {
										const status = shipmentStatusEnums?.[id];
										const isActive = Number(id) === details.shipmentState;
										return (
											<Button
												key={`key=${id}`}
												disabled={!isActive}
												id={isActive ? "active-status" : ""}
											>
												{status}
											</Button>
										);
									})}
								</ButtonGroup>
							)}
						</div>
					</div>
					<div>{childMapper[details.shipmentState]}</div>
				</CardBody>
			</Card>
		);
	},
);

ShipmentProgress.displayName = "ShipmentProgress";
